// Cookie-backed storage adapter for cross-subdomain SSO (subdomain routing mode).
//
// Implements the getItem/setItem/removeItem interface that Auth (auth.ts) already uses for its token
// cache, but persists to a cookie scoped to the parent domain (e.g. ".dev.tryorion.xyz") so a session
// established on the auth origin (the Console apex) is readable by every <app>.dev.tryorion.xyz. This
// is what lets ONE Cognito callback serve unlimited app subdomains.
//
// Why a cookie and not localStorage: localStorage is origin-scoped (orion.dev.tryorion.xyz can't read
// the apex's), whereas a Domain=.dev.tryorion.xyz cookie is shared across all subdomains. It must be
// JS-readable (a public SPA reads the token to send as a bearer), so it is NOT HttpOnly — see the
// security notes in the plan/CLAUDE.md. It's first-party/same-site, so Safari ITP doesn't apply.
//
// SECURITY TRADEOFF (accepted): because the cookie is parent-domain-scoped AND JS-readable, an XSS
// in ANY one tenant subdomain can read the shared session (incl. the refresh token) and impersonate
// the user across every app. This is inherent to "one Cognito callback for unlimited subdomains".
// Mitigations already in place: SameSite=Lax + Secure, a shortened (7d) refresh TTL, and the
// short-lived PKCE verifier/CSRF nonce kept OUT of this cookie (origin-scoped sessionStorage only).
// The real fix (if this blast radius becomes unacceptable) is a token-broker/BFF: hold the refresh
// token in an HttpOnly cookie on a first-party auth service and hand out short-lived access tokens —
// tracked as a hardening follow-up, not a drop-in change here.
//
// Cookie size: a single cookie caps ~4KB and a Cognito {id,access,refresh} JSON can exceed that, so
// the token key is SPLIT — id+access+expiry in one cookie, refresh_token in a second. Tiny keys (the
// PKCE verifier, the post-login target) must NOT live here (they belong in the apex's own
// sessionStorage during the brief login window); this adapter only backs the tokens key.

import type { CookieDocLike, StorageLike } from "./types";

const TOKENS_KEY = "atlas.tokens";
const RT_COOKIE = "atlas_rt"; // refresh_token, split out to stay under the per-cookie size cap
const TOKENS_COOKIE = "atlas_tokens"; // id_token + access_token + expiresAt

// SameSite=Lax (NOT Strict): the auth origin -> app return is a top-level GET navigation, which Lax
// allows but Strict would strip on the first cross-site hop. Secure: HTTPS only.
function cookieAttrs(domain: string): string {
  return `; Domain=${domain}; Path=/; Secure; SameSite=Lax`;
}

function readCookie(doc: CookieDocLike, name: string): string | null {
  const items = (doc.cookie || "").split(";");
  for (const item of items) {
    const idx = item.indexOf("=");
    if (idx === -1) continue;
    if (item.slice(0, idx).trim() === name) {
      return decodeURIComponent(item.slice(idx + 1));
    }
  }
  return null;
}

function writeCookie(
  doc: CookieDocLike,
  name: string,
  value: string,
  domain: string,
  maxAgeSeconds?: number,
): void {
  const age = maxAgeSeconds === undefined ? "" : `; Max-Age=${maxAgeSeconds}`;
  doc.cookie = `${name}=${encodeURIComponent(value)}${cookieAttrs(domain)}${age}`;
}

function deleteCookie(doc: CookieDocLike, name: string, domain: string): void {
  doc.cookie = `${name}=${cookieAttrs(domain)}; Max-Age=0`;
}

// makeCookieStorage({ doc, domain }) -> { getItem, setItem, removeItem }.
// `doc` is the document (injected for tests); `domain` is the leading-dot parent (".dev.tryorion.xyz").
export function makeCookieStorage({ doc, domain }: { doc: CookieDocLike; domain: string }): StorageLike {
  return {
    getItem(key) {
      if (key !== TOKENS_KEY) {
        // Only the tokens key is cookie-backed; anything else (shouldn't occur) reads null.
        return null;
      }
      const main = readCookie(doc, TOKENS_COOKIE);
      if (!main) return null;
      let parsed;
      try {
        parsed = JSON.parse(main);
      } catch {
        return null;
      }
      const rt = readCookie(doc, RT_COOKIE);
      if (rt) parsed.refresh_token = rt;
      return JSON.stringify(parsed);
    },

    setItem(key, value) {
      if (key !== TOKENS_KEY) return;
      let obj;
      try {
        obj = JSON.parse(value);
      } catch {
        return;
      }
      // Bound cookie lifetime to the refresh-token window so stale cookies don't linger forever.
      const maxAge = 7 * 24 * 60 * 60; // 7 days; matches the shortened Cognito refresh TTL
      const { refresh_token: rt, ...rest } = obj;
      writeCookie(doc, TOKENS_COOKIE, JSON.stringify(rest), domain, maxAge);
      if (rt) writeCookie(doc, RT_COOKIE, rt, domain, maxAge);
    },

    removeItem(key) {
      if (key !== TOKENS_KEY) return;
      deleteCookie(doc, TOKENS_COOKIE, domain);
      deleteCookie(doc, RT_COOKIE, domain);
    },
  };
}
